import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import SVGIcon, { IIcons } from './icons';
import Text from './text';
import { EditableChangeEvent } from '../../utils/types/editable';
import Label from './text/label';

interface SelectProps {
	options: readonly string[];
	isMulti?: boolean;
	id?: string;
	name?: string;
	value?: string | number | string[];
	icons?: IIcons[];
	onChange?: (e: EditableChangeEvent) => void;
	disabled?: boolean;
	className?: string;
	boxClassName?: string;
	placeholder?: string;
	placeholderClassName?: string;
	optionClassName?: string;
	optionItemClassName?: string;
	optionItemSelectedClassName?: string;
	menuClassName?: string;
	title?: string;
	label?: string;
	required?: boolean;
}

const Select = ({
	options,
	name,
	id,
	value,
	onChange,
	className,
	boxClassName,
	placeholder,
	placeholderClassName,
	isMulti = false,
	icons,
	title,
	disabled,
	label,
	required,
	optionClassName,
	optionItemClassName,
	optionItemSelectedClassName,
	menuClassName,
}: SelectProps) => {
	const [isVisible, setIsVisible] = useState(false);
	const selectRef = useRef<HTMLDivElement>(null);
	const optionsRef = useRef<HTMLUListElement>(null);
	const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});

	// Calcula a posição do menu
	const calculateMenuPosition = useCallback(() => {
		if (!selectRef.current) return;

		const rect = selectRef.current.getBoundingClientRect();
		const windowHeight = window.innerHeight;
		const scrollY = window.scrollY;

		// Altura aproximada do menu (máx 12.625rem = 202px)
		const menuHeight = Math.min(options.length * 40, 202);
		const spaceBelow = windowHeight - rect.bottom;
		const spaceAbove = rect.top;

		// Decide se deve abrir para cima ou para baixo
		const shouldOpenAbove =
			menuHeight > spaceBelow && spaceAbove > spaceBelow;

		setMenuStyles({
			position: 'absolute',
			top: shouldOpenAbove ? 'auto' : `${rect.bottom + scrollY}px`,
			bottom: shouldOpenAbove
				? `${windowHeight - rect.top - scrollY}px`
				: 'auto',
			left: `${rect.left + scrollX}px`,
			width: `${rect.width}px`,
			marginTop: shouldOpenAbove ? '0' : '4px',
			marginBottom: shouldOpenAbove ? '4px' : '0',
		});
	}, [options.length]);

	// Alterna a visibilidade do menu
	const toggleMenu = () => {
		if (!disabled) {
			const newVisibility = !isVisible;
			setIsVisible(newVisibility);
			if (newVisibility) {
				// Pequeno delay para garantir que o DOM foi atualizado
				setTimeout(calculateMenuPosition, 0);
			}
		}
	};

	// Atualiza posição quando o menu é aberto ou a tela é redimensionada/scrollada
	useEffect(() => {
		if (!isVisible) return;

		const handleUpdatePosition = () => {
			calculateMenuPosition();
		};

		// Atualiza posição inicial
		handleUpdatePosition();

		// Adiciona listeners para eventos que podem afetar a posição
		window.addEventListener('resize', handleUpdatePosition);
		window.addEventListener('scroll', handleUpdatePosition, true);

		return () => {
			window.removeEventListener('resize', handleUpdatePosition);
			window.removeEventListener('scroll', handleUpdatePosition, true);
		};
	}, [isVisible, calculateMenuPosition]);

	// Fecha o menu ao clicar fora
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				selectRef.current &&
				!selectRef.current.contains(event.target as Node) &&
				optionsRef.current &&
				!optionsRef.current.contains(event.target as Node)
			) {
				setIsVisible(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	// Verifica se o item é selecionado
	const isSelected = useCallback(
		(item: string) =>
			isMulti
				? (value as string[])?.includes(item)
				: item === String(value),
		[isMulti, value]
	);

	const handleSelect = useCallback(
		(selectedValue: string) => {
			if (isMulti) {
				const currentValue = (value as string[]) || [];
				const newValue = currentValue.includes(selectedValue)
					? currentValue.filter((item) => item !== selectedValue)
					: [...currentValue, selectedValue];
				onChange?.({
					target: { name, value: newValue },
				} as unknown as React.ChangeEvent<HTMLInputElement>);
			} else {
				onChange?.({
					target: { name, value: selectedValue },
				} as React.ChangeEvent<HTMLInputElement>);
				setIsVisible(false);
			}
		},
		[isMulti, name, value, onChange]
	);

	const multiRemoveItem = (index: number) => {
		const currentValue = (value as string[]) || [];
		currentValue.splice(index, 1);
		onChange?.({
			target: { name, value: currentValue },
		} as unknown as React.ChangeEvent<HTMLInputElement>);
		setIsVisible(false);
	};

	const shouldRenderLabel = () =>
		label &&
		((Array.isArray(value) ? value.length > 0 : value) || disabled);

	const renderPlaceholder = useMemo(
		() => (
			<div className="flex items-center space-x-2 overflow-hidden">
				{!isMulti ? (
					<>
						{icons && value && (
							<SVGIcon
								className="w-5 h-auto flex-shrink-0"
								icon={icons[options.indexOf(value as string)]}
							/>
						)}
						<Text
							className={classNames(
								'text-ellipsis whitespace-nowrap',
								{
									'text-gray-label':
										!value && options.length > 0,
									'text-gray': options.length == 0,
									'select-none': disabled,
									invisible: disabled && !value,
								},
								placeholderClassName
							)}
						>
							{value ? (
								value
							) : (
								<>
									{placeholder}
									{required && (
										<Text className="text-red ml-1">*</Text>
									)}
								</>
							)}
						</Text>
					</>
				) : (
					<Text
						className={classNames(
							'flex gap-1 text-ellipsis whitespace-nowrap flex-wrap',
							{
								'text-gray-label': options.length > 0,
								'text-gray': options.length == 0,
								'select-none': disabled,
								invisible:
									disabled &&
									(!value || (value as string[]).length == 0),
							},
							placeholderClassName
						)}
					>
						{(value as string[]).length == 0
							? placeholder
							: (value as string[]).map((item, index) => (
									<Text
										className="flex items-center justify-between gap-1 bg-gray-bg px-2 rounded-lg cursor-default"
										key={index}
									>
										{item}
										{!disabled && (
											<div className="flex items-center justify-center size-3 rounded-full bg-gray-border hover:opacity-70">
												<SVGIcon
													className="w-2 h-auto fill-gray-label cursor-pointer"
													icon="X"
													onClick={(e) => {
														e.stopPropagation();
														multiRemoveItem(index);
													}}
												/>
											</div>
										)}
									</Text>
								))}
					</Text>
				)}
			</div>
		),
		[
			icons,
			value,
			options,
			placeholder,
			disabled,
			placeholderClassName,
			isMulti,
			required,
		]
	);

	const renderOptions = useMemo(
		() =>
			options.map((option, index) => (
				<li
					key={`option-${index}`}
					className={classNames(
						'px-3 py-2 cursor-pointer transition-colors',
						{
							'hover:bg-gray-bg': !optionItemClassName,
							'bg-gray-bg pointer-events-none':
								isSelected(option) &&
								!optionItemSelectedClassName,
							'pointer-events-none':
								isSelected(option) &&
								optionItemSelectedClassName,
						},
						isSelected(option) && optionItemSelectedClassName,
						optionItemClassName
					)}
					onClick={() => handleSelect(option)}
					role="option"
					aria-selected={isSelected(option)}
				>
					<div className="flex items-center gap-2">
						{icons && (
							<SVGIcon
								className="w-5 h-auto flex-shrink-0"
								icon={icons[index]}
							/>
						)}
						<Text
							className={classNames(
								'text-ellipsis whitespace-nowrap',
								optionClassName
							)}
						>
							{option}
						</Text>
					</div>
				</li>
			)),
		[
			options,
			icons,
			isSelected,
			handleSelect,
			optionClassName,
			optionItemClassName,
			optionItemSelectedClassName,
		]
	);

	return (
		<div
			className={classNames('relative h-fit min-w-fit', className)}
			ref={selectRef}
		>
			{/* Label opcional */}
			{shouldRenderLabel() && (
				<Label
					className={classNames('absolute -top-2 text-xs bg-white', {
						'left-3 px-1': !disabled,
					})}
				>
					{label}
				</Label>
			)}

			{/* Caixa de seleção */}
			<div
				className={classNames(
					'flex items-center justify-between py-2 bg-white w-full',
					{
						'border border-gray-border px-3 cursor-pointer rounded-md select-none':
							!disabled,
						'border-gray-bg': options.length == 0,
						'pointer-events-none': disabled || options.length == 0,
					},
					boxClassName
				)}
				onClick={toggleMenu}
				aria-haspopup="listbox"
				aria-expanded={isVisible}
			>
				{renderPlaceholder}
				{!disabled && (
					<SVGIcon
						className={classNames(
							'w-3 h-auto fill-gray-label transition-transform',
							{
								'rotate-180': isVisible,
							}
						)}
						icon="ArrowTriangle"
					/>
				)}
				<input type="hidden" id={id} name={name} value={value} />
			</div>

			{/* Opções - Renderizado via Portal para evitar problemas com overflow */}
			{isVisible &&
				ReactDOM.createPortal(
					<ul
						ref={optionsRef}
						className={classNames(
							'fixed bg-white border border-gray-border rounded-md shadow-lg z-[9999] max-h-[12.625rem] overflow-x-hidden overflow-y-auto',
							menuClassName
						)}
						style={menuStyles}
						role="listbox"
						title={title}
						aria-labelledby={id}
					>
						{renderOptions}
					</ul>,
					document.body
				)}
		</div>
	);
};

export default Select;
